import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { AuthenticatedProvider } from "./contexts/AuthenticatedContext";

// Layout and pages
import AppLayout from "./components/layout/AppLayout";
import Home from "./pages/HomePage";
import WorkspaceRouter from "./pages/workspace/WorkspaceRouter";

import "./styles/main.css";

// Lazy-loaded pages with explicit chunk names
const LoginPage = lazy(() => 
  import(/* webpackChunkName: "login" */ "./pages/LoginPage")
);
const AssetsPage = lazy(() => 
  import(/* webpackChunkName: "assets" */ "./pages/AssetsPage")
);
const PresetsPage = lazy(() => 
  import(/* webpackChunkName: "presets" */ "./pages/PresetsPage")
);
const ProjectsPage = lazy(() => 
  import(/* webpackChunkName: "projects" */ "./pages/ProjectsPage")
);
const SettingsPage = lazy(() => 
  import(/* webpackChunkName: "settings" */ "./pages/SettingsPage")
);

// Initialize theme from localStorage (if present)
const savedTheme = localStorage.getItem("theme-storage");
if (savedTheme) {
  const { state } = JSON.parse(savedTheme);
  document.documentElement.setAttribute("data-theme", state.theme);
}

// Loading fallback component
const LoadingFallback = () => (
  <div className="h-screen w-screen flex items-center justify-center">
    <div className="loading loading-spinner loading-lg text-primary" />
  </div>
);

/**
 * The main App component just defines the routes.
 * We wrap our lazy-loaded routes with Suspense to provide a fallback.
 */
export default function App() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public route: Login */}
        <Route
          path="/login"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <LoginPage />
            </Suspense>
          }
        />

        {/* Protected Routes: RequireAuth + layout */}
        <Route
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Home />} />
          <Route
            path="assets"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <AssetsPage />
              </Suspense>
            }
          />
          <Route
            path="presets"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <PresetsPage />
              </Suspense>
            }
          />
          <Route
            path="projects"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <ProjectsPage />
              </Suspense>
            }
          />
          <Route
            path="settings"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <SettingsPage />
              </Suspense>
            }
          />
          <Route path="workspace/*" element={<WorkspaceRouter />} />
        </Route>

        {/* Catch-all redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Checks for Firebase auth. If not logged in (and not still loading),
 * redirects to /login; otherwise, renders the children.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isInitialized } = useAuth();
  const location = useLocation();

  // Don't render anything while Firebase is initializing
  if (!isInitialized) {
    return null;
  }

  // Show loading state while checking auth
  if (isLoading) {
    return <LoadingFallback />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render authenticated content
  return <AuthenticatedProvider>{children}</AuthenticatedProvider>;
}
